import { Button, Heading, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useGetSharedHomeworksPaginated from "../../hooks/api/homework/useGetSharedHomeworksPaginated";
import { SharedHomework } from "../../models/SharedHomework";
import ErrorSection from "../ErrorSection";
import SharedHomeworkItem from "../items/SharedHomeworkItem";
import LoadingSection from "../LoadingSection";

export default function SharedHomeworks() {
  const { t } = useTranslation();
  const {
    data: lastFetchedSharedHomeworks,
    mutateAsync: getSharedHomeworks,
    isPending,
    isError,
    variables,
  } = useGetSharedHomeworksPaginated();
  const [sharedHomeworks, setSharedHomeworks] = useState<SharedHomework[]>([]);

  const updateSharedHomeworks = useCallback(
    (pageNumber = 0) => {
      getSharedHomeworks({
        searchType: "BY_SESSION_USER",
        pageable: {
          pageNumber,
          pageSize: 10,
          paged: true,
          offset: 0,
          sort: [],
          unpaged: true,
        },
      }).then((newSharedHomeworks) =>
        setSharedHomeworks((oldSharedHomeworks) => [
          ...oldSharedHomeworks,
          ...newSharedHomeworks.content,
        ]),
      );
    },
    [getSharedHomeworks],
  );

  const loadMore = useCallback(() => {
    updateSharedHomeworks((variables?.pageable.pageNumber ?? 0) + 1);
  }, [updateSharedHomeworks, variables?.pageable.pageNumber]);

  useEffect(() => {
    updateSharedHomeworks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isPending && (variables.pageable.pageNumber ?? 0) === 0)
    return <LoadingSection />;

  if (isError)
    return <ErrorSection retry={() => getSharedHomeworks(variables)} />;

  return (
    <VStack gap={4}>
      <Heading alignSelf="flex-start" mb={8} as="h1" size="lg" fontWeight="500">
        {t("student_learning_paths_page.bulletin_board")}
      </Heading>
      {!sharedHomeworks.length ? (
        <Text>{t("student_learning_paths_page.no_shared_homeworks")}</Text>
      ) : (
        sharedHomeworks.map((sharedHomework) => (
          <SharedHomeworkItem
            key={sharedHomework.sharingCode}
            sharedHomework={sharedHomework}
          />
        ))
      )}
      {(variables?.pageable.pageNumber ?? 0) + 2 <
        (lastFetchedSharedHomeworks?.page.totalPages ?? 0) && (
        <Button my={8} isLoading={isPending} onClick={loadMore}>
          {t("common.buttons.load_more")}
        </Button>
      )}
    </VStack>
  );
}
